import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateMerchantMutation } from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';

type UseMerchantCreationReturn = {
  createMerchant: (
    merchantName: string,
  ) => Promise<{ id: string; name: string } | null>;
  isCreatingMerchant: boolean;
};

export const useMerchantCreation = (): UseMerchantCreationReturn => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const [createMerchantMutation, { loading: isCreatingMerchant }] =
    useCreateMerchantMutation();

  const createMerchant = useCallback(
    async (
      merchantName: string,
    ): Promise<{ id: string; name: string } | null> => {
      try {
        const result = await createMerchantMutation({
          variables: {
            input: {
              name: merchantName,
            },
          },
        });

        const merchant = result.data?.expenses?.createMerchant?.merchant;
        const error = result.data?.expenses?.createMerchant?.error;

        if (error || !merchant?.Id) {
          addError(error || t('expenses.form.merchantCreationFailed'));
          return null;
        }

        return {
          id: merchant.Id,
          name: merchant.Name || merchantName,
        };
      } catch (err) {
        addError(t('expenses.form.merchantCreationFailed'));
        return null;
      }
    },
    [createMerchantMutation, addError, t],
  );

  return {
    createMerchant,
    isCreatingMerchant,
  };
};
